import { useState } from 'react';
import styles from './EditLabel.module.scss';

export const LABEL_COLORS = [
  '#DADADA',
  '#BB8C7D',
  '#F8AFA0',
  '#FED49A',
  '#9EFAD7',
  '#95C5FD',
  '#FFAEED',
  '#FF8484',
] as const;

export type LabelColor = (typeof LABEL_COLORS)[number];

export interface EditLabelProps {
  /** Initial label text */
  initialLabel?: string;
  /** Initial selected color (hex) */
  initialColor?: string;
  /** Callback when the user confirms */
  onConfirm: (label: string, color: LabelColor) => void;
  /** Callback when the user cancels */
  onCancel: () => void;
  /** Custom CSS class */
  className?: string;
}

export const EditLabel = ({
  initialLabel = '',
  initialColor = LABEL_COLORS[0],
  onConfirm,
  onCancel,
  className = '',
}: EditLabelProps) => {
  const [label, setLabel] = useState(initialLabel);
  const [selectedColor, setSelectedColor] = useState<LabelColor>(
    (LABEL_COLORS as readonly string[]).includes(initialColor)
      ? (initialColor as LabelColor)
      : LABEL_COLORS[0]
  );

  const handleConfirm = () => {
    if (label.trim()) {
      onConfirm(label.trim(), selectedColor);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className={`${styles.editLabel} ${className}`}>
      <p className={styles.title}>Edit label</p>

      <div className={styles.inputContainer}>
        <input
          type="text"
          name="Edit label name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      </div>

      <div className={styles.swatches} role="radiogroup" aria-label="Label colour">
        {LABEL_COLORS.map((hex) => (
          <button
            key={hex}
            type="button"
            className={`${styles.swatch} ${selectedColor === hex ? styles.swatchSelected : ''}`}
            onClick={() => setSelectedColor(hex)}
            aria-label={hex}
            aria-checked={selectedColor === hex}
            role="radio"
          >
            <span className={styles.swatchInner} style={{ backgroundColor: hex }} />
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <button type="button" className="btn btn-small btn-secondary w100" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-small btn-primary w100" onClick={handleConfirm}>
          Confirm
        </button>
      </div>
    </div>
  );
};
